import React from "react";
import { PaginationConfig } from "../types";

interface TablePaginationProps {
  paginationConfig: PaginationConfig;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}

const TablePagination: React.FC<TablePaginationProps> = ({
  paginationConfig,
  onPageChange,
  onPageSizeChange,
}) => {
  const { currentPage, pageSize, totalItems, pageSizeOptions } =
    paginationConfig;
  const totalPages = Math.ceil(totalItems / pageSize);

  return (
    <div className="flex flex-col items-center justify-between p-4 bg-white border-t md:flex-row">
      <div className="flex items-center mb-4 text-xs text-gray-700 sm:text-sm md:mb-0">
        <span className="flex items-center">
          Showing
          <span className="mx-1 font-medium">
            {(currentPage - 1) * pageSize + 1}
          </span>
          -
          <span className="mx-1 font-medium">
            {Math.min(currentPage * pageSize, totalItems)}
          </span>
          of
          <span className="mx-1 font-medium">{totalItems}</span>
          results
        </span>
      </div>

      <div className="flex flex-col items-center sm:flex-row sm:space-x-3">
        <div className="inline-flex items-center mb-3 sm:mb-0">
          <label className="hidden mr-2 text-sm font-medium text-gray-700 sm:inline">
            Rows per page:
          </label>
          <select
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 py-1.5 px-3"
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
          >
            {pageSizeOptions.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>

        <nav aria-label="Page navigation">
          <ul className="inline-flex -space-x-px text-sm">
            <li>
              <button
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={`flex items-center justify-center px-3 h-8 ml-0 leading-tight text-gray-500 bg-white border border-gray-300 rounded-l-lg ${
                  currentPage === 1
                    ? "cursor-not-allowed opacity-50"
                    : "hover:bg-gray-100 hover:text-gray-700"
                }`}
              >
                <span className="sr-only">Previous</span>
                <svg
                  className="w-3 h-3"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 6 10"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M5 1 1 5l4 4"
                  />
                </svg>
              </button>
            </li>

            {currentPage > 2 && (
              <>
                <li>
                  <button
                    onClick={() => onPageChange(1)}
                    className="flex items-center justify-center h-8 px-3 leading-tight text-gray-500 bg-white border border-gray-300 hover:bg-gray-100 hover:text-gray-700"
                  >
                    1
                  </button>
                </li>
                {currentPage > 3 && (
                  <li>
                    <span className="flex items-center justify-center h-8 px-3 leading-tight text-gray-500 bg-white border border-gray-300">
                      ...
                    </span>
                  </li>
                )}
              </>
            )}

            {[...Array(3)].map((_, i) => {
              const pageToShow = currentPage - 1 + i;
              return (
                pageToShow > 0 &&
                pageToShow <= totalPages && (
                  <li key={pageToShow}>
                    <button
                      onClick={() => onPageChange(pageToShow)}
                      className={`flex items-center justify-center px-3 h-8 leading-tight ${
                        pageToShow === currentPage
                          ? "text-blue-600 border border-gray-300 bg-blue-50"
                          : "text-gray-500 bg-white border border-gray-300 hover:bg-gray-100 hover:text-gray-700"
                      }`}
                    >
                      {pageToShow}
                    </button>
                  </li>
                )
              );
            })}

            {currentPage < totalPages - 1 && (
              <>
                {currentPage < totalPages - 2 && (
                  <li>
                    <span className="flex items-center justify-center h-8 px-3 leading-tight text-gray-500 bg-white border border-gray-300">
                      ...
                    </span>
                  </li>
                )}
                <li>
                  <button
                    onClick={() => onPageChange(totalPages)}
                    className="flex items-center justify-center h-8 px-3 leading-tight text-gray-500 bg-white border border-gray-300 hover:bg-gray-100 hover:text-gray-700"
                  >
                    {totalPages}
                  </button>
                </li>
              </>
            )}

            <li>
              <button
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`flex items-center justify-center px-3 h-8 leading-tight text-gray-500 bg-white border border-gray-300 rounded-r-lg ${
                  currentPage === totalPages
                    ? "cursor-not-allowed opacity-50"
                    : "hover:bg-gray-100 hover:text-gray-700"
                }`}
              >
                <span className="sr-only">Next</span>
                <svg
                  className="w-3 h-3"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 6 10"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="m1 9 4-4-4-4"
                  />
                </svg>
              </button>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  );
};

export default TablePagination;
